
import React, { useState, useRef, useEffect } from 'react';
import { Disc, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { toast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

const MusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [trackInfo, setTrackInfo] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const discRef = useRef<HTMLDivElement | null>(null);
  const streamUrl = 'https://cast1.torontocast.com:4640/stream';
  const isMobile = useIsMobile();

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch((error) => {
          console.error("Ошибка воспроизведения:", error);
          toast({
            title: "Ошибка воспроизведения",
            description: "Не удалось воспроизвести поток. Пожалуйста, попробуйте еще раз.",
            variant: "destructive"
          });
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      audioRef.current.volume = volume / 100;
    }
  }, [isMuted, volume]);

  useEffect(() => {
    if (!audioRef.current) return;

    const fetchMetadata = () => {
      if (isPlaying && audioRef.current) {
        try {
          const currentTrack = audioRef.current.title || audioRef.current.dataset.title || '';
          if (currentTrack && currentTrack !== trackInfo) {
            setTrackInfo(currentTrack);
            console.log('Current track:', currentTrack);
          }
        } catch (error) {
          console.error('Failed to get metadata:', error);
        }
      }
    };

    audioRef.current.addEventListener('loadedmetadata', fetchMetadata);
    audioRef.current.addEventListener('playing', fetchMetadata);

    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => {
        setIsPlaying(true);
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        setIsPlaying(false);
      });
    }

    const metadataInterval = setInterval(fetchMetadata, 5000);

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('loadedmetadata', fetchMetadata);
        audioRef.current.removeEventListener('playing', fetchMetadata);
      }
      clearInterval(metadataInterval);
    };
  }, [isPlaying, trackInfo]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
    if (value[0] > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-b from-wax-50 to-honey-50 rounded-xl shadow-lg border border-honey-200">
      <div className="relative mb-8">
        <div
          ref={discRef}
          className={`w-64 h-64 rounded-full bg-[#d5b289] flex items-center justify-center shadow-lg relative transition-all duration-500 ${isPlaying ? 'animate-spin' : ''}`}
          style={{ animationDuration: '8s', transformOrigin: 'center center' }}
        >
          <div className="w-56 h-56 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-[6px] border-[#c49c6c] opacity-70"></div>
          <div className="w-44 h-44 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-[6px] border-[#c49c6c] opacity-70"></div>
          <div className="w-32 h-32 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-[6px] border-[#c49c6c] opacity-70"></div>

          <div className="w-16 h-16 rounded-full bg-[#b56f10] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
            <div className="w-4 h-4 rounded-full bg-[#8b4f00]"></div>
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={streamUrl}
        onError={(e) => {
          console.error("Audio error:", e);
          toast({
            title: "Ошибка аудио",
            description: "Произошла ошибка при загрузке аудиопотока",
            variant: "destructive"
          });
        }}
      />

      <div className="flex flex-col items-center w-full max-w-xs mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <Button
            onClick={togglePlay}
            variant="outline"
            className="rounded-full w-14 h-14 flex items-center justify-center bg-honey-500 hover:bg-honey-600 border-none text-black shadow-md transform hover:scale-105 transition-all"
          >
            {isPlaying ? <Pause size={28} /> : <Play size={28} />}
          </Button>

          <Button
            onClick={toggleMute}
            variant="outline"
            className="rounded-full w-12 h-12 flex items-center justify-center bg-wax-100 hover:bg-wax-200 border-wax-300 text-wax-800 shadow-sm"
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </Button>
        </div>

        <div className="w-full flex items-center space-x-3">
          <Volume2 size={16} className="text-wax-600" />
          <Slider
            defaultValue={[volume]}
            max={100}
            step={1}
            value={[volume]}
            onValueChange={handleVolumeChange}
            className="flex-1"
          />
        </div>
      </div>

      <div className="text-center text-wax-800">
        <p className="text-xl font-cormorant-garamond font-medium mb-1">BUDNIK Bee Garden Radio</p>
        <p className="text-sm opacity-75">Релаксирующая музыка для души</p>
        <p className="mt-4 text-xs text-wax-600">
          {isPlaying ? "Сейчас играет" : "Нажмите Play, чтобы начать прослушивание"}
        </p>
      </div>
    </div>
  );
};

export default MusicPlayer;
